import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Button from '../../../components/elements/button';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import { useFacilitiesApi } from '../../../store/api/hooks/facilitiesApiHook';

interface UserFacilityFormProps {
    _ref?: any;
    editData?: any;
    onCreate?: () => void;
    onCancel?: () => void;
}

type UserFacilityFormValueType = {
    facilityManagerName: string;
    phoneNumber: string;
    email: string;
};

const CreateMasterFacility: React.FC<UserFacilityFormProps> = ({ _ref, editData, onCreate = () => { }, onCancel = () => { } }) => {
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<UserFacilityFormValueType>({
        defaultValues: {
            facilityManagerName: '',
            phoneNumber: '',
            email: ''
        }
    });

    const { showLoader, hideLoader } = useAppLoader();
    const facilityApi = useFacilitiesApi();

    const formReset = () => {
        reset({
            facilityManagerName: '',
            phoneNumber: '',
            email: ''
        });
    };

    const onSubmit: SubmitHandler<UserFacilityFormValueType> = formData => {
        showLoader();
        
        const payload = {
            facilityManagerName: formData.facilityManagerName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            ...(editData && { facilityManagerID: editData.facilityManagerID })
        };

        facilityApi.saveFacilityMaster(
            payload,
            (response: any) => {
                hideLoader();
                const action = editData ? 'updated' : 'created';
                toast.success(`Facility Manager ${action} successfully`);
                onCreate();
                onCancel();
                formReset();
            },
            (error: any) => {
                hideLoader();
                toast.error(error || 'Failed to save facility manager');
            }
        );
    };

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = formReset;
        }
    }, [_ref]);

    useEffect(() => {
        if (editData == null) {
            formReset();
        }
    }, [editData]);

    useEffect(() => {
        if (editData) {
            setValue('facilityManagerName', editData.name || editData.facilityManagerName || '');
            setValue('phoneNumber', editData.phone || editData.phoneNumber || '');
            setValue('email', editData.emailID || editData.email || '');
        } else {
            formReset();
        }
    }, [editData]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="flex flex-col h-full">
            <div className="flex-1 overflow-auto">
                <Controller
                    name="facilityManagerName"
                    control={control}
                    rules={{ required: "Facility Manager Name is required" }}
                    render={({ field: { value, onChange } }) => (
                        <Input
                            label="Facility Manager Name"
                            type="text"
                            value={value}
                            onChange={onChange}
                            required
                            error={errors.facilityManagerName?.message}
                        />
                    )}
                />
            
                <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Input
                            label="Phone Number"
                            type="text"
                            value={value}
                            onChange={onChange}
                            placeholder="+1 (555) 123-4567"
                        />
                    )}
                />
            
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                        }
                    }}
                    render={({ field: { value, onChange } }) => (
                        <Input
                            label="Email"
                            type="email"
                            value={value}
                            onChange={onChange}
                            placeholder="example@facility.com"
                            error={errors.email?.message}
                        />
                    )}
                />
            </div>

            <div className="flex justify-end gap-2 flex-shrink-0 pt-2 border-t border-gray-200">
                <Button type="button" theme="secondary" onClick={formReset}>
                    Reset
                </Button>
                <Button type="submit">
                    Save
                </Button>
            </div>
        </form>
    );
};

export default CreateMasterFacility;